import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, makeStyles, TextareaAutosize, Tooltip } from '@material-ui/core'
import clsx from 'clsx'
import { useDropzone } from 'react-dropzone'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import SydButton from '../../../../components/commonDesign/Button'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import { uploadDocument } from '../../../../service'
import { useUploadAttachment } from '../../../../api/denaliAssistant'
import { useVoiceInput } from '../../../../hooks/useVoiceInput'
import Disclaimer from './Disclaimer'

const useStyles = makeStyles((theme) => ({
  inputContainer: {
    backgroundColor: 'transparent'
  },
  inputContent: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto'
  },
  inputWrapper: {
    backgroundColor: 'var(--dai-color-input)',
    color: 'var(--font-color-main)',
    border: 'var(--dai-border-accent)',
    boxShadow: 'var(--shadow-sm)',
    borderRadius: '12px',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease',
    position: 'relative',
    '&:focus-within': {
      borderColor: 'var(--dai-color-border-accent-hl)'
    }
  },
  spinningGradientWrapper: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-2px',
      borderRadius: '14px',
      padding: '2px',
      background: 'conic-gradient(from 0deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      animation: '$spinGradient 3s linear infinite',
      pointerEvents: 'none'
    }
  },
  '@keyframes spinGradient': {
    '0%': {
      background: 'conic-gradient(from 0deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '25%': {
      background: 'conic-gradient(from 90deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '50%': {
      background: 'conic-gradient(from 180deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '75%': {
      background: 'conic-gradient(from 270deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    },
    '100%': {
      background: 'conic-gradient(from 360deg, var(--palette-purple-500), var(--palette-blue-500), var(--palette-purple-500))'
    }
  },
  dragActive: {
    borderColor: 'var(--dai-color-border-accent-hl)',
    backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 8%, var(--dai-color-input))'
  },
  sending: {
    color: 'color-mix(var(--font-color-main) 50%, transparent)',
    backgroundColor: 'color-mix(in srgb, var(--color-primary-500) 5%, var(--dai-color-input))'
  },
  sendingCompact: {
    minHeight: '32px',
    maxHeight: '32px',
    padding: '0',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '& > *': {
      opacity: 0,
      transform: 'scale(0.95)'
    }
  },
  textarea: {
    width: '100%',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: '14px',
    lineHeight: 1.6,
    color: 'inherit',
    fontFamily: 'inherit',
    '&::placeholder': {
      color: 'var(--dai-color-text-low)'
    }
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  tools: {
    display: 'flex',
    gap: '8px'
  },
  toolButton: {
    padding: '6px',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--dai-color-text-low)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d3)',
      color: 'var(--color-primary-500)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  toolButtonListening: {
    color: 'var(--color-danger-500)',
    animation: '$pulse 1.5s ease-in-out infinite'
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1
    },
    '50%': {
      opacity: 0.6
    }
  },
  charCount: {
    fontSize: '12px',
    color: 'var(--dai-color-text-low)'
  },
  submitSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  keyboardHint: {
    fontSize: '12px',
    color: 'var(--dai-color-text-low)'
  },
  attachmentIconWrapper: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    height: 'fit-content',
    '&:hover $removeIcon': {
      display: 'flex'
    }
  },
  attachmentIcon: {
    padding: '6px',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--dai-color-text-low)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d3)',
      color: 'var(--color-primary-500)'
    }
  },
  removeIcon: {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-primary-500)',
    color: 'var(--color-contrast-primary-500)',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '8px',
    lineHeight: 1,
    boxShadow: 'var(--shadow-sm)',
    opacity: 0.7,
    pointerEvents: 'none',
    '& svg': {
      fontSize: '8px'
    }
  },
  progressIcon: {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}))

const MAX_LENGTH = 4000

const MessageInput = forwardRef(({ onSend, disabled, placeholder, className, style, minRows, submissionMode }, ref) => {
  const classes = useStyles()
  const textareaRef = useRef(null)
  const fileInputRef = useRef(null)
  const uploadAttachmentMutation = useUploadAttachment()
  // Fully self-contained state - no parent re-renders
  const [value, setValue] = React.useState('')
  const [charCount, setCharCount] = React.useState(0)
  const [hasContent, setHasContent] = React.useState(false)
  // Attachment state: { file: File, status: 'uploading' | 'uploaded', s3Key?: string }
  const [attachments, setAttachments] = useState([])
  const [sending, setSending] = useState(false)
  const _disable = useMemo(() => sending || disabled, [sending, disabled])

  // Voice input hook
  const handleTranscriptUpdate = useCallback((newValue) => {
    setValue(newValue)
    setCharCount(newValue.length)
    setHasContent(!!newValue.trim())
  }, [])

  const getCurrentValue = useCallback(() => {
    return textareaRef.current?.value || value
  }, [value])

  const { isListening, isSpeechSupported, handleVoiceToggle } = useVoiceInput({
    getCurrentValue,
    handleTranscriptUpdate,
    maxLength: MAX_LENGTH
  })

  // Expose imperative methods to parent
  useImperativeHandle(ref, () => ({
    setValue: (newValue) => {
      const trimmedValue = newValue.slice(0, MAX_LENGTH)
      setValue(trimmedValue)
      setCharCount(trimmedValue.length)
      setHasContent(!!trimmedValue.trim())
      // Focus the textarea
      if (textareaRef.current) {
        textareaRef.current.focus()
        // Move cursor to end
        textareaRef.current.setSelectionRange(trimmedValue.length, trimmedValue.length)
      }
    },
    focus: () => {
      textareaRef.current?.focus()
    },
    clear: () => {
      setValue('')
      setCharCount(0)
      setHasContent(false)
      setAttachments([])
      setSending(false)
      // Voice input will stop automatically when cleared via hook
      if (isListening) {
        handleVoiceToggle()
      }
    }
  }), [isListening, handleVoiceToggle])

  const handleKeyDown = useCallback((e) => {
    // Submit on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const isUploading = attachments.some(a => a.status === 'uploading')
      if (value.trim() && !_disable && !isUploading) {
        const messageAttachments = attachments
          .filter(a => a.status === 'uploaded' && a.s3Key)
          .map(a => ({
            title: a.fileName,
            contentType: a.contentType,
            data: {
              key: a.s3Key
            }
          }))

        setSending(true)
        onSend(value, messageAttachments)
      }
    }
  }, [value, _disable, onSend, attachments, setSending])

  const handleSubmit = useCallback(() => {
    const isUploading = attachments.some(a => a.status === 'uploading')
    if (value.trim() && !_disable && !isUploading) {
      // Stop voice recognition if active
      if (isListening) {
        handleVoiceToggle()
      }

      // Build attachments array for the message
      const messageAttachments = attachments
        .filter(a => a.status === 'uploaded' && a.s3Key)
        .map(a => ({
          title: a.fileName,
          contentType: a.contentType,
          data: {
            key: a.s3Key
          }
        }))

      // TODO: Pass attachments to onSend when backend is ready
      // For now, just send the message text

      setSending(true)
      onSend(value, messageAttachments)
    }
  }, [value, _disable, setSending, onSend, attachments, isListening, handleVoiceToggle])

  const handleFileAttach = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(async (fileOrEvent) => {
    // Handle both file input events and dropzone file arrays
    let file
    if (fileOrEvent?.target?.files) {
      // File input event
      file = fileOrEvent.target.files[0]
    } else if (Array.isArray(fileOrEvent) && fileOrEvent.length > 0) {
      // Dropzone files array
      file = fileOrEvent[0]
    } else if (fileOrEvent instanceof File) {
      // Direct file object
      file = fileOrEvent
    }

    if (file) {
      const attachment = {
        file,
        status: 'uploading',
        fileName: file.name,
        contentType: file.type || 'application/octet-stream'
      }
      setAttachments([attachment])

      try {
        // Get presigned URL from backend
        const data = await uploadAttachmentMutation.mutateAsync({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream'
        })

        const { url, key } = data

        // Upload file to S3 using presigned URL
        await uploadDocument(url, file, undefined, undefined)

        // Update attachment with S3 key
        setAttachments([{
          ...attachment,
          status: 'uploaded',
          s3Key: key
        }])
      } catch (error) {
        console.error('Failed to upload file:', error)
        // Remove attachment on error
        setAttachments([])
        // TODO: Show error toast to user
      }

      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [uploadAttachmentMutation])

  const handleRemoveAttachment = useCallback(() => {
    setAttachments([])
  }, [])

  // Define accepted file types as array of MIME types
  const acceptedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/*',
    'application/xml',
    'text/xml',
    'application/json',
    'text/json'
  ]

  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled,
    onDropAccepted: handleFileChange,
    multiple: false,
    accept: acceptedFileTypes
  })

  const handleChange = useCallback((e) => {
    const newValue = e.target.value.slice(0, MAX_LENGTH)
    setValue(newValue)
    setCharCount(newValue.length)
    setHasContent(!!newValue.trim())
  }, [])

  const remainingChars = MAX_LENGTH - charCount
  const isNearLimit = remainingChars < 100
  const isUploading = attachments.some(a => a.status === 'uploading')
  const uploadedAttachment = attachments.find(a => a.status === 'uploaded')

  return (
    <FadeIn className={clsx(classes.inputContainer, className)} style={style}>
      <div className={classes.inputContent}>
        <div
          {...getRootProps()}
          className={clsx(classes.inputWrapper, {
            [classes.dragActive]: isDragActive,
            [classes.sending]: _disable && submissionMode === 'inline',
            [classes.sendingCompact]: _disable && submissionMode === 'external',
            [classes.spinningGradientWrapper]: _disable && submissionMode === 'external'
          })}
        >
          <TextareaAutosize
            ref={textareaRef}
            className={classes.textarea}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={_disable}
            minRows={minRows}
            maxRows={10}
          />

          <div className={classes.actions}>
            <div className={classes.tools}>
              {isSpeechSupported && (
                <Tooltip title={isListening ? 'Stop voice input' : 'Start voice input'} arrow>
                  <button
                    className={clsx(classes.toolButton, {
                      [classes.toolButtonListening]: isListening
                    })}
                    onClick={handleVoiceToggle}
                    disabled={_disable || isUploading}
                    type='button'
                  >
                    <Icon
                      name={isListening ? ICON_NAMES.mic : ICON_NAMES.micNone}
                      style={{ fontSize: '20px', color: 'inherit' }}
                    />
                  </button>
                </Tooltip>
              )}
              <input
                ref={fileInputRef}
                type='file'
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept='.pdf,.jpg,.jpeg,.gif,.png,.webp,text/*,.xml,.json'
              />
              <Tooltip title={uploadedAttachment ? 'Only one file supported' : 'Attach file'} arrow>
                <button
                  className={classes.toolButton}
                  onClick={handleFileAttach}
                  disabled={_disable || isUploading || !!uploadedAttachment}
                >
                  <Icon name='add' style={{ fontSize: '20px', color: 'inherit' }} />
                </button>
              </Tooltip>
              {isUploading && (
                <div className={classes.progressIcon}>
                  <CircularProgress size={16} style={{ color: 'var(--dai-color-text-low)' }} />
                </div>
              )}
              {uploadedAttachment && (
                <Tooltip title={uploadedAttachment.fileName} arrow>
                  <div className={classes.attachmentIconWrapper}>
                    <button
                      className={classes.attachmentIcon}
                      onClick={handleRemoveAttachment}
                      disabled={_disable}
                      title={`Remove ${uploadedAttachment.fileName}`}
                    >
                      <Icon name='file' style={{ fontSize: '20px', color: 'inherit' }} />
                    </button>
                    <div className={classes.removeIcon}>
                      <Icon name='close' style={{ fontSize: '8px', color: 'inherit' }} />
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>

            <div className={classes.submitSection}>
              <span
                className={classes.charCount} style={{
                  color: isNearLimit ? 'var(--color-warning-500)' : undefined
                }}
              >
                {remainingChars}
              </span>
              <span className={classes.keyboardHint}>
                {window.navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
              </span>
              <SydButton
                size='sm'
                variant='primary'
                onClick={handleSubmit}
                disabled={_disable || !hasContent || isUploading}
                processing={sending}
              >
                <Icon name='navigateNext' style={{ fontSize: '18px', fill: 'var(--color-contrast-primary-500)' }} />
              </SydButton>
            </div>
          </div>
        </div>
      </div>
      {submissionMode === 'external' ? <Disclaimer /> : null}
    </FadeIn>
  )
})

MessageInput.displayName = 'MessageInput'

MessageInput.propTypes = {
  onSend: PropTypes.func.isRequired, // (message: string, attachments?: Array) => void
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  minRows: PropTypes.number,
  submissionMode: PropTypes.oneOf(['inline', 'external'])
}

MessageInput.defaultProps = {
  disabled: false,
  placeholder: 'Type a message...',
  minRows: 1,
  submissionMode: 'inline'
}

export default MessageInput
